// ============================================================================
// Shopee — Webhook adapter (Push V2 notifications).
//
// Shopee pushes notifications with:
//   - Header `Authorization: <signature>`
//     HMAC-SHA256 of `url|request_body` (pipe-separated) signed with the
//     partner_key. Encoded as BASE64 (NOT hex — distinct from the outbound
//     request-signing path which uses hex).
//   - Body: JSON
//       {
//         "shop_id": 123,
//         "code": 1,                       // event type code
//         "timestamp": 1681689600,         // provider-assigned dispatch time
//         "data": { "order_sn": "...", ... }
//       }
//
// Common event codes (from Shopee Push V2 docs):
//   1  → order status update (fetch full order separately; push only has order_sn)
//   3  → logistics status update (tracking number / ship status)
//   4  → shop update
//   5  → banned item
//   8  → item status update
//   9  → shop promotion
//   10 → shop authorization deauthed
//
// We map the subset we care about to capability events; unknown codes still
// flow through the ingress pipeline (audit log) but do NOT emit on the typed
// bus (the `capability` field is left undefined).
//
// Retry-stable event id:
//   `${shop_id}:${code}:${timestamp}:${data.order_sn || data.item_id}`.
//   Shopee re-pushes on 5xx responses (or when we fail to 2xx inside the
//   timeout window); the same quadruple comes back each time so dedupe is
//   reliable.
// ============================================================================

import {
  base64ToBytes,
  hmac,
  timingSafeEqualBytes,
} from '../../services/webhook-verifier'
import type {
  ParsedWebhookEvent,
  WebhookAdapter,
  WebhookAdapterContext,
} from '../../services/webhook-adapters'
import { ShopeeSignatureError } from './types'

// ============================================================================
// Public: verify Shopee Push signature
// ============================================================================

/**
 * Verify a Shopee push notification signature in constant time.
 *
 * Base string: `${webhookUrl}|${rawBody}`. HMAC-SHA256 with partnerKey → base64.
 *
 * Timing-safe compare via `timingSafeEqualBytes` — never a string `===`
 * (would leak the partner_key byte-by-byte through differential timing).
 *
 * Returns `false` on any malformed / missing input so the ingress pipeline
 * treats verification failures uniformly.
 */
export async function verifyShopeePush(
  rawBody: ArrayBuffer | Uint8Array | string,
  signatureHeader: string | null | undefined,
  webhookUrl: string,
  partnerKey: string,
): Promise<boolean> {
  if (!signatureHeader || typeof signatureHeader !== 'string') return false
  if (!webhookUrl || typeof webhookUrl !== 'string') return false
  if (!partnerKey || typeof partnerKey !== 'string') return false

  const bodyBytes = toBytes(rawBody)
  const bodyText = new TextDecoder().decode(bodyBytes)
  const baseString = `${webhookUrl}|${bodyText}`
  const expected = await hmac(
    partnerKey,
    new TextEncoder().encode(baseString),
    'SHA-256',
  )

  let candidate: Uint8Array
  try {
    candidate = base64ToBytes(signatureHeader.trim())
  } catch {
    return false
  }
  return timingSafeEqualBytes(candidate, expected)
}

// ============================================================================
// Parse
// ============================================================================

export interface ShopeePushEnvelope {
  shop_id?: number | string
  code?: number
  timestamp?: number
  data?: {
    order_sn?: string
    item_id?: number | string
    status?: string
    [k: string]: unknown
  } & Record<string, unknown>
}

/**
 * Parse a raw Shopee Push body into one or more `ParsedWebhookEvent`s.
 *
 * The ingress pipeline calls this AFTER signature verification so we don't
 * need to re-verify here. Throws `ShopeeSignatureError` on malformed / missing
 * envelope fields so the ingress pipeline can distinguish "bad envelope" from
 * "unknown event code".
 */
export function parseShopeePushBody(
  rawBody: ArrayBuffer | Uint8Array | string,
): ParsedWebhookEvent[] {
  const text = new TextDecoder().decode(toBytes(rawBody))
  let envelope: ShopeePushEnvelope
  try {
    envelope = JSON.parse(text) as ShopeePushEnvelope
  } catch {
    throw new ShopeeSignatureError('Shopee push body is not valid JSON')
  }

  const shopId = envelope?.shop_id
  const code = envelope?.code
  const timestamp = envelope?.timestamp
  if (
    (typeof shopId !== 'number' && typeof shopId !== 'string') ||
    typeof code !== 'number' ||
    typeof timestamp !== 'number'
  ) {
    throw new ShopeeSignatureError('Shopee push envelope missing shop_id/code/timestamp')
  }

  const shopIdStr = String(shopId)
  const data = envelope.data ?? {}
  const orderSn = typeof data.order_sn === 'string' ? data.order_sn : undefined
  const itemId = data.item_id !== undefined ? String(data.item_id) : undefined
  const dedupeTail = orderSn ?? itemId ?? 'none'
  const eventId = `${shopIdStr}:${code}:${timestamp}:${dedupeTail}`
  const receivedAt = new Date().toISOString()

  switch (code) {
    case 1: {
      // Order status update — push carries only order_sn + status. The
      // dispatcher fetches the full order via `getShopeeOrderDetail` before
      // emitting the `commerce.order.updated` capability event. We parse to
      // a structured payload so downstream code doesn't have to re-parse the
      // raw envelope.
      if (!orderSn) {
        throw new ShopeeSignatureError('Shopee push code=1 missing data.order_sn')
      }
      return [
        {
          eventId,
          eventType: 'commerce.order.updated',
          // We deliberately do NOT set `capability` here — the resolution
          // job fetches the full order + emits the typed `commerce.order.*`
          // event with the hydrated payload. The ingress pipeline persists
          // this row for audit but doesn't fan out to subscribers.
          payload: {
            code,
            shopId: shopIdStr,
            orderSn,
            status: typeof data.status === 'string' ? data.status : undefined,
            timestamp,
            receivedAt,
          },
        },
      ]
    }
    case 3: {
      // Logistics status update. Same pattern — the resolver pulls the fresh
      // order and emits a single `commerce.order.updated` with
      // `changedFields: ['trackingNumber', 'status']`.
      if (!orderSn) {
        throw new ShopeeSignatureError('Shopee push code=3 missing data.order_sn')
      }
      return [
        {
          eventId,
          eventType: 'commerce.logistics.updated',
          payload: {
            code,
            shopId: shopIdStr,
            orderSn,
            timestamp,
            receivedAt,
          },
        },
      ]
    }
    case 8: {
      // Item status update — emits `commerce.product.updated` downstream. No
      // capability-event mapping here; we persist for the resolver job.
      if (!itemId) {
        throw new ShopeeSignatureError('Shopee push code=8 missing data.item_id')
      }
      return [
        {
          eventId,
          eventType: 'commerce.product.updated',
          payload: {
            code,
            shopId: shopIdStr,
            itemId,
            timestamp,
            receivedAt,
          },
        },
      ]
    }
    case 10: {
      // Shop deauthorized — the shop owner revoked the app. The ingress
      // pipeline logs and the scheduler marks the instance `disconnected`.
      return [
        {
          eventId,
          eventType: 'commerce.shop.deauthorized',
          payload: { code, shopId: shopIdStr, timestamp, receivedAt },
        },
      ]
    }
    default: {
      // Unknown event codes — surface an audit-only row. Returning empty
      // here would make Shopee think delivery failed and trigger retry.
      return [
        {
          eventId,
          eventType: `commerce.shopee.code_${code}`,
          payload: { code, shopId: shopIdStr, timestamp, receivedAt, data },
        },
      ]
    }
  }
}

// ============================================================================
// Adapter wiring
// ============================================================================

async function verifyWebhook(ctx: WebhookAdapterContext): Promise<boolean> {
  const signature = ctx.headers.get('authorization')
  // Pull the partner_key from the env (operator-level secret, not per-instance).
  // Same variable name as the seed's `oauth_config.partnerKeyEnv`.
  const partnerKey =
    typeof ctx.env.SHOPEE_PARTNER_KEY === 'string' ? ctx.env.SHOPEE_PARTNER_KEY : ''
  if (!partnerKey) return false

  // The webhook URL the operator configured — Shopee includes it in the
  // signature base string. We read it from the per-instance config so each
  // instance can have its own ingress URL.
  const webhookUrl = pickWebhookUrl(ctx.instance.config)
  if (!webhookUrl) return false

  const hmacOk = await verifyShopeePush(ctx.rawBody, signature, webhookUrl, partnerKey)
  if (!hmacOk) return false

  // Round 1 C1 fix: partner_key is operator-global across every shop that
  // linked to our Shopee partner app, so an HMAC-valid body could claim any
  // `shop_id`. Enforce that the push body's `shop_id` matches the shop we
  // provisioned this instance against — anything else is a spoof attempt by
  // another authorized seller aiming at the wrong instance URL.
  const expectedShopId = pickConfiguredShopId(ctx.instance.config)
  if (!expectedShopId) return false // fail closed when instance config is malformed

  const bodyShopId = pickShopIdFromBody(ctx.rawBody)
  if (!bodyShopId || String(bodyShopId) !== String(expectedShopId)) {
    return false
  }

  return true
}

async function parseWebhook(ctx: WebhookAdapterContext): Promise<ParsedWebhookEvent[]> {
  return parseShopeePushBody(ctx.rawBody)
}

/**
 * Surface `parseShopeePushBody` under the adapter-pattern name expected by
 * the test suite + the public surface.
 */
export function parseShopeeWebhook(
  ctx: WebhookAdapterContext,
): Promise<ParsedWebhookEvent[]> {
  return parseWebhook(ctx)
}

function pickWebhookUrl(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string' || raw.length === 0) return null
  let parsed: Record<string, unknown>
  try {
    parsed = JSON.parse(raw) as Record<string, unknown>
  } catch {
    return null
  }
  const url = parsed.push_webhook_url ?? parsed.webhook_url
  if (typeof url === 'string' && url.length > 0) {
    return canonicaliseWebhookUrl(url)
  }
  return null
}

/**
 * Issue #255 L2: Shopee's signature base string is `${url}|${body}` against
 * the EXACT URL the push hit. Any mismatch — trailing slash, query string,
 * fragment — breaks verification. Operators sometimes paste a URL that
 * carries a `?foo=1` debug param or trailing slash; normalising to
 * `origin + pathname` at read time removes the footgun.
 *
 * If the raw string isn't a valid URL we bail to the original value — bad
 * URLs flow through to `verifyShopeePush` where they'll fail signature
 * verification anyway.
 */
function canonicaliseWebhookUrl(raw: string): string {
  try {
    const u = new URL(raw)
    return u.origin + u.pathname.replace(/\/$/, '')
  } catch {
    return raw
  }
}

/** Round 1 C1: extract the shop_id this instance was provisioned against. */
function pickConfiguredShopId(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string' || raw.length === 0) return null
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>
    const id = parsed.shop_id ?? parsed.shopId
    if (typeof id === 'string' && id.length > 0) return id
    if (typeof id === 'number' && Number.isFinite(id)) return String(id)
  } catch {
    return null
  }
  return null
}

/**
 * Round 1 C1: read `shop_id` from the push envelope BEFORE we trust it.
 * We re-parse the raw body here because `verifyWebhook` sits upstream of the
 * `parseWebhook` step, so the envelope isn't cached for us. Cheap enough —
 * the body is small and JSON.parse is the same parser downstream uses.
 */
function pickShopIdFromBody(
  raw: ArrayBuffer | Uint8Array | string,
): string | null {
  const bytes = toBytes(raw)
  const text = new TextDecoder().decode(bytes)
  try {
    const parsed = JSON.parse(text) as Record<string, unknown>
    const id = parsed.shop_id
    if (typeof id === 'string' && id.length > 0) return id
    if (typeof id === 'number' && Number.isFinite(id)) return String(id)
  } catch {
    return null
  }
  return null
}

function toBytes(raw: ArrayBuffer | Uint8Array | string): Uint8Array {
  if (typeof raw === 'string') return new TextEncoder().encode(raw)
  if (raw instanceof Uint8Array) return raw
  return new Uint8Array(raw)
}

// ============================================================================
// Adapter export
// ============================================================================

export const shopeeWebhookAdapter: WebhookAdapter = {
  verifyWebhook,
  parseWebhook,
}
